import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InternalEmailUser {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  avatar_url?: string;
}

export interface InternalEmailOrgRole {
  id: number;
  name: string;
}

/** One recipient row as the prefill returns it. */
export interface InternalEmailRecipientSeed {
  users?: InternalEmailUser[];
  participant_groups?: string[];
  org_roles?: InternalEmailOrgRole[];
}

/** A candidate document the sender may attach — names and ids only. */
export interface InternalEmailDocument {
  blob_id?: number;
  ats_attachment_id?: number;
  filename?: string;
  content_type?: string;
  byte_size?: number;
  /** Expiring link, so the composer can open a document before attaching it. */
  url?: string;
}

/**
 * Where the scorecards export has got to. Unlike the candidate's own files this one
 * does not exist until someone asks for it, so the row carries a state instead of
 * always carrying a file.
 *
 *   ready        rendered and still current — a real file, with a size and a link
 *   generating   a render is in flight; poll until it lands
 *   pending      there is feedback to export, nothing rendered — picking it starts one
 *   unavailable  no submitted feedback, so there is nothing to export
 */
export type InternalEmailScorecardsStatus =
  | 'ready'
  | 'generating'
  | 'pending'
  | 'unavailable';

export interface InternalEmailScorecards extends InternalEmailDocument {
  ref?: string;
  status?: InternalEmailScorecardsStatus;
}

export interface InternalEmailDocuments {
  offer_documents?: InternalEmailDocument[];
  resume?: InternalEmailDocument | null;
  scorecards?: InternalEmailScorecards | null;
  /** Superseded by `scorecards`; true when there is feedback to export. */
  scorecards_available?: boolean;
  other_files?: InternalEmailDocument[];
}

export interface InternalEmailPrefill {
  sender?: InternalEmailUser | null;
  ats_shared_email?: { id: number; email?: string; name?: string } | null;
  to?: InternalEmailRecipientSeed;
  cc?: InternalEmailRecipientSeed;
  bcc?: InternalEmailRecipientSeed;
  subject?: string | null;
  body?: string | null;
  template_attachments?: InternalEmailDocument[];
  documents?: InternalEmailDocuments;
  /**
   * The document kinds the activity was configured to attach.
   *
   * A default, not a limit: `documents` above lists everything the candidate
   * has, whatever this says, and these are the rows the composer opens with
   * ticked. Absent on a backend that has not shipped core#5987, where nothing
   * is pre-ticked and `documents` is the narrower set it always was.
   */
  attach_document_kinds?: string[];
  template_category?: string;
}

export interface InternalEmailMessage {
  id: number;
  stage?: string;
  sent_at?: string | null;
  user?: InternalEmailUser;
}

/**
 * One attachment as the show payload returns it — no URL, names and sizes only.
 *
 * `id` is the ActiveStorage attachment's and cannot be sent back; an edit names a
 * file again by `blob_id` (or `signed_id`), the same handles the compose prefill
 * returns for the activity's template attachments.
 */
export interface InternalEmailAttachment {
  id: number;
  blob_id?: number;
  signed_id?: string;
  filename?: string;
  content_type?: string;
  byte_size?: number;
  /** Expiring link, so the composer can open a document before attaching it. */
  url?: string;
}

export interface InternalEmail {
  id: number;
  stage?: string;
  status?: string;
  scheduled_at?: string | null;
  sent_at?: string | null;
  timezone?: string | null;
  subject?: string;
  body?: string;
  /** Who it went out as. Only the show payload carries these. */
  sender?: InternalEmailUser | null;
  ats_shared_email?: { id: number; email?: string; name?: string } | null;
  attachments?: InternalEmailAttachment[];
  /** Candidate documents the sender picked, already resolved to blobs. */
  document_blob_ids?: number[];
  include_scorecards?: boolean;
  /**
   * The library template the composer applied, when it applied one. Its files are
   * not the candidate's, so the API has no other way to let them through the
   * attachment allow-list.
   */
  ats_comm_template_id?: number | string | null;
  /** Who each row went to — the people the roles resolved to at compose time. */
  to?: InternalEmailUser[];
  cc?: InternalEmailUser[];
  bcc?: InternalEmailUser[];
  /**
   * What each row was composed from — the people picked, and the roles as the tokens
   * they were picked as — in the shape the compose prefill uses, so an edit seeds the
   * same way and a role comes back as its chip rather than as whoever it covered.
   */
  recipients?: {
    to?: InternalEmailRecipientSeed;
    cc?: InternalEmailRecipientSeed;
    bcc?: InternalEmailRecipientSeed;
  };
  messages?: InternalEmailMessage[];
}

/** What the send form submits. Roles resolve to people server-side, at send time. */
export interface SendInternalEmailPayload {
  ats_application_id: number | string;
  /** Absent on an ad-hoc send, which has no activity behind it. */
  ats_job_stage_action_id?: number | string;
  sender_id?: number | string | null;
  ats_shared_email_id?: number | string | null;
  to_user_ids?: (string | number)[];
  to_participant_groups?: string[];
  to_org_role_ids?: number[];
  cc_user_ids?: (string | number)[];
  cc_participant_groups?: string[];
  cc_org_role_ids?: number[];
  bcc_user_ids?: (string | number)[];
  bcc_participant_groups?: string[];
  bcc_org_role_ids?: number[];
  subject?: string;
  body?: string;
  scheduled_at?: string | null;
  timezone?: string | null;
  /** Existing candidate documents, attached by reference. */
  ats_attachment_ids?: number[];
  offer_document_blob_ids?: number[];
  include_resume?: boolean;
  include_scorecards?: boolean;
  /** Newly uploaded files, which are the only ones carrying bytes. */
  /** Uploads, plus the blob ids of files already on the email being edited. */
  attachments?: (File | number)[];
  /**
   * The internal email this one replies to. The API threads the reply under it in
   * the candidate's conversations, and asks the mailbox to thread it too.
   */
  reply_to_ats_user_message_id?: number | string;
}

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

const appendList = (
  formData: FormData,
  key: string,
  values?: (string | number)[]
) => {
  if (!values) return;
  // Multipart drops a key with no values, and the API reads a missing list as
  // "fall back to the activity" rather than "the sender cleared it" — so an
  // empty list still has to put something on the wire. The API strips the
  // marker back out; sending it alongside real values would fail :integer.
  if (values.length === 0) {
    formData.append(`${key}[]`, '');
    return;
  }
  values.forEach((value) => formData.append(`${key}[]`, String(value)));
};

export const buildSendInternalEmailFormData = (
  payload: SendInternalEmailPayload
): FormData => {
  const formData = new FormData();

  formData.append('ats_application_id', String(payload.ats_application_id));
  // Omitted entirely on an ad-hoc send — sending "undefined" would be read as
  // an activity id and 404.
  if (payload.ats_job_stage_action_id) {
    formData.append(
      'ats_job_stage_action_id',
      String(payload.ats_job_stage_action_id)
    );
  }

  if (payload.sender_id)
    formData.append('sender_id', String(payload.sender_id));
  if (payload.ats_shared_email_id) {
    formData.append('ats_shared_email_id', String(payload.ats_shared_email_id));
  }

  (['to', 'cc', 'bcc'] as const).forEach((prefix) => {
    appendList(formData, `${prefix}_user_ids`, payload[`${prefix}_user_ids`]);
    appendList(
      formData,
      `${prefix}_participant_groups`,
      payload[`${prefix}_participant_groups`]
    );
    appendList(
      formData,
      `${prefix}_org_role_ids`,
      payload[`${prefix}_org_role_ids`]
    );
  });

  if (payload.subject !== undefined) {
    formData.append('subject', payload.subject ?? '');
  }
  if (payload.body !== undefined) formData.append('body', payload.body ?? '');
  if (payload.scheduled_at) {
    formData.append('scheduled_at', payload.scheduled_at);
  }
  if (payload.timezone) formData.append('timezone', payload.timezone);

  if (payload.ats_comm_template_id) {
    formData.append(
      'ats_comm_template_id',
      String(payload.ats_comm_template_id)
    );
  }

  if (payload.reply_to_ats_user_message_id) {
    formData.append(
      'reply_to_ats_user_message_id',
      String(payload.reply_to_ats_user_message_id)
    );
  }

  appendList(formData, 'ats_attachment_ids', payload.ats_attachment_ids);
  appendList(
    formData,
    'offer_document_blob_ids',
    payload.offer_document_blob_ids
  );
  if (payload.include_resume !== undefined) {
    formData.append('include_resume', String(Boolean(payload.include_resume)));
  }
  if (payload.include_scorecards !== undefined) {
    formData.append(
      'include_scorecards',
      String(Boolean(payload.include_scorecards))
    );
  }

  // Same marker as the id lists: an edit that removed every file has to say so, or
  // the API falls back to the activity's own attachments. A blob id goes as a string,
  // which the API resolves the same way it resolves a signed id.
  if (payload.attachments?.length === 0) {
    formData.append('attachments[]', '');
  } else {
    payload.attachments?.forEach((file) =>
      formData.append(
        'attachments[]',
        typeof file === 'number' ? String(file) : file
      )
    );
  }

  return formData;
};

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

export async function fetchInternalEmailPrefill(params: {
  applicationId: number | string;
  activityId?: number | string;
}): Promise<InternalEmailPrefill> {
  const query = new URLSearchParams({
    ats_application_id: String(params.applicationId),
  });
  // An ad-hoc send has no activity; the key is left off rather than sent empty.
  if (params.activityId) {
    query.set('ats_job_stage_action_id', String(params.activityId));
  }

  const res = (await http.get(`/internal_emails/new?${query.toString()}`)) as {
    data?: InternalEmailPrefill;
  };
  assertResolvedAtsNotHttpError(res);
  return res?.data ?? {};
}

/**
 * Asks the server to render the candidate's scorecards so they can be attached as a
 * file rather than a promise. Answers with the row's new state — usually `generating`,
 * or `ready` when an export it can reuse is already there.
 *
 * Safe to call again: the gate is server-side, so re-ticking the row or a racing poll
 * never queues a second render.
 */
export async function startInternalEmailScorecardsPdf(params: {
  applicationId: number | string;
  activityId?: number | string;
}): Promise<InternalEmailScorecards> {
  const res = (await http.post('/internal_emails/scorecards_pdf', {
    ats_application_id: params.applicationId,
    ...(params.activityId
      ? { ats_job_stage_action_id: params.activityId }
      : {}),
  })) as { data?: { scorecards?: InternalEmailScorecards } };
  assertResolvedAtsNotHttpError(res);
  return res?.data?.scorecards ?? {};
}

export async function sendInternalEmail(
  payload: SendInternalEmailPayload
): Promise<InternalEmail> {
  const res = (await http.post(
    '/internal_emails',
    buildSendInternalEmailFormData(payload)
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function fetchInternalEmail(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.get(
    `/internal_emails/${encodeURIComponent(String(id))}`
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function cancelInternalEmail(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.post(`/internal_emails/${id}/cancel`)) as {
    data?: InternalEmail;
  };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function updateInternalEmail({
  id,
  payload,
}: {
  id: number | string;
  payload: SendInternalEmailPayload;
}): Promise<InternalEmail> {
  const res = (await http.patch(
    `/internal_emails/${encodeURIComponent(String(id))}`,
    buildSendInternalEmailFormData(payload)
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function sendInternalEmailNow(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.post(`/internal_emails/${id}/send_now`)) as {
    data?: InternalEmail;
  };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const internalEmailQueryKeys = {
  all: ['internal-emails'] as const,
  prefill: (applicationId: number | string, activityId: number | string) =>
    [
      ...internalEmailQueryKeys.all,
      'prefill',
      applicationId,
      activityId,
    ] as const,
  detail: (id: number | string) =>
    [...internalEmailQueryKeys.all, 'detail', id] as const,
};

// Gotenberg renders in 3-15s. 4s keeps the card's loader feeling responsive without
// hammering the prefill endpoint — the same interval the offer picker polls at.
export const SCORECARDS_POLL_INTERVAL_MS = 4000;

/** True while the scorecards export is mid-render, which is the only state that resolves on its own. */
export const isScorecardsGenerating = (prefill?: InternalEmailPrefill) =>
  prefill?.documents?.scorecards?.status === 'generating';

/**
 * Seeds the compose slider from the activity's configuration.
 *
 * Not cached between openings: the roles configured on the activity resolve to
 * whoever holds them right now, and the candidate's documents change as the
 * team works the application.
 */
export function useInternalEmailPrefill(params: {
  applicationId?: number | string;
  activityId?: number | string;
  enabled?: boolean;
}): UseQueryResult<InternalEmailPrefill, Error> {
  const { applicationId, activityId, enabled = true } = params;

  return useQuery({
    queryKey: internalEmailQueryKeys.prefill(
      applicationId ?? '',
      activityId ?? ''
    ),
    queryFn: () =>
      fetchInternalEmailPrefill({
        applicationId: applicationId as number | string,
        activityId: activityId as number | string,
      }),
    enabled: Boolean(enabled && applicationId),
    staleTime: 0,
    // Poll only while the scorecards export is being rendered, and stop the moment it
    // is not. `ready` needs no more fetches; `pending` and `unavailable` never change
    // on their own — something has to ask for a render — so polling those would run
    // forever for nothing.
    refetchInterval: (query: { state: { data?: InternalEmailPrefill } }) =>
      isScorecardsGenerating(query.state.data)
        ? SCORECARDS_POLL_INTERVAL_MS
        : false,
  });
}

/**
 * One sent internal email, for the read-only viewer.
 *
 * Unlike the prefill this is a record of what went out, so it is safe to cache:
 * the copy is fixed once sent. Per-recipient delivery can still move while
 * copies are in flight, which is what `staleTime` keeps short.
 */
export function useInternalEmail(
  id?: number | string,
  options: { enabled?: boolean } = {}
): UseQueryResult<InternalEmail, Error> {
  const { enabled = true } = options;

  return useQuery({
    queryKey: internalEmailQueryKeys.detail(id ?? ''),
    queryFn: () => fetchInternalEmail(id as number | string),
    enabled: Boolean(enabled && id),
    staleTime: 30_000,
  });
}

/**
 * Starts the scorecards render and re-reads the prefill, which is what puts the row
 * into `generating` and so starts the poll above. Without the invalidate the composer
 * would sit on the `pending` it had when the sender ticked the row.
 */
export function useStartInternalEmailScorecardsPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startInternalEmailScorecardsPdf,
    onSettled: (_data, _error, variables) =>
      queryClient.invalidateQueries({
        queryKey: internalEmailQueryKeys.prefill(
          variables.applicationId ?? '',
          variables.activityId ?? ''
        ),
      }),
  });
}

export function useSendInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useUpdateInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useSendInternalEmailNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendInternalEmailNow,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useCancelInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}
